import { SettingsRowFrame } from '@/components/settings/settings-row-frame'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

type WeightSegment = { key: string; label: string; weight: number }

type WeightAllocationBarProps = {
  /** Fraction 0–1 */
  total: number
  segments: WeightSegment[]
  onNormalize?: () => void
  onWeightChange?: (key: string, weight: number) => void
  label?: string
  /** When true, omit outer border — for use inside SettingsPanel. */
  embedded?: boolean
}

export function WeightAllocationBar({
  total,
  segments,
  onNormalize,
  onWeightChange,
  label = 'Weight allocation',
  embedded = false,
}: WeightAllocationBarProps) {
  const pct = Math.round(total * 100)
  const ok = total <= 0 || Math.abs(total - 1) <= 0.05
  const colors = [
    'bg-primary',
    'bg-primary/80',
    'bg-primary/60',
    'bg-primary/40',
    'bg-primary/30',
    'bg-muted-foreground/40',
  ]

  const inner = (
    <div className="space-y-2 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p>
          {label}: <span className="font-medium">{pct}%</span> / 100%
          {!ok ? (
            <span className="ml-2 text-warning">Adjust weights to sum to ~100%</span>
          ) : null}
        </p>
        {onNormalize ? (
          <Button type="button" variant="outline" size="sm" onClick={onNormalize}>
            Balance to 100%
          </Button>
        ) : null}
      </div>
      {segments.length > 0 ? (
        <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted">
          {segments.map((seg, i) => (
            <div
              key={seg.key}
              className={`${colors[i % colors.length]} h-full`}
              style={{ width: `${Math.max(0, Math.min(100, (seg.weight || 0) * 100))}%` }}
              title={`${seg.label}: ${Math.round((seg.weight || 0) * 100)}%`}
            />
          ))}
        </div>
      ) : null}
      {onWeightChange && segments.length > 0 ? (
        <div className="grid gap-2 sm:grid-cols-2">
          {segments.map((seg) => (
            <div key={seg.key} className="flex items-center gap-2">
              <Label htmlFor={`weight-${seg.key}`} className="min-w-0 flex-1 truncate text-xs">
                {seg.label}
              </Label>
              <div className="flex items-center gap-1 shrink-0">
                <Input
                  id={`weight-${seg.key}`}
                  type="number"
                  min={0}
                  max={100}
                  step={5}
                  className="h-8 w-16 text-xs tabular-nums"
                  value={Math.round((seg.weight || 0) * 100)}
                  onChange={(e) => {
                    const next = Number(e.target.value)
                    if (!Number.isFinite(next)) return
                    onWeightChange(seg.key, Math.max(0, Math.min(100, next)) / 100)
                  }}
                />
                <span className="text-xs text-muted-foreground">%</span>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )

  if (embedded) {
    return (
      <SettingsRowFrame
        showDivider={false}
        innerClassName={cn('py-3', ok ? 'bg-muted/20' : 'bg-warning/5')}
      >
        {inner}
      </SettingsRowFrame>
    )
  }

  return (
    <div
      className={cn(
        'rounded-md border px-3 py-2 space-y-2 text-sm',
        ok ? 'bg-muted/30' : 'border-warning/50 bg-warning/5',
      )}
    >
      {inner}
    </div>
  )
}
